import { Button } from '@/components/ui/button';
import { MobileCarousel } from '@/components/MobileCarousel';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Check, Building2, Shield, Award, Star, FileCheck } from 'lucide-react';

export default function CompanyFormationPage({ onCTAClick }) {
  return (
    <div>
      <section className="py-20 md:py-32 bg-gradient-to-br from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://images.pexels.com/photos/8373815/pexels-photo-8373815.jpeg" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-accent mb-4">Company Formation Services</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-primary mb-6 leading-tight">
              Professional LLC and Corporation Formation Services
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-8 leading-relaxed">
              Start your business the right way with expert company formation services. We handle all documentation, state filing, and compliance requirements to get your LLC or Corporation legally established quickly and efficiently.
            </p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white" onClick={onCTAClick}>
              Start My Business
            </Button>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
                Complete Business Formation Services
              </h2>
              <p className="text-base leading-relaxed text-muted-foreground mb-6">
                Company formation is the legal process of creating a business entity such as an LLC (Limited Liability Company) or Corporation. Proper business formation protects your personal assets from business liabilities, provides tax advantages, enhances business credibility, and establishes a formal business structure for growth and investment.
              </p>
              <p className="text-base leading-relaxed text-muted-foreground">
                Our company formation services cover every aspect of starting a business: business name registration and availability checks, Articles of Organization/Incorporation preparation and filing, registered agent service, EIN (Employer Identification Number) application with the IRS, operating agreements and corporate bylaws, and compliance with state filing requirements. Whether you're launching a startup, formalizing a sole proprietorship, or establishing a new business venture, we provide the expert guidance and documentation services you need to get started legally and properly.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-sm overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.08)]">
                <img src="https://images.pexels.com/photos/32319625/pexels-photo-32319625.jpeg" alt="Business startup" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Why Professional Company Formation Matters
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Shield, title: 'Personal Asset Protection', desc: 'LLCs and Corporations provide limited liability protection, separating your personal assets from business debts and legal obligations.' },
              { icon: Award, title: 'Tax Advantages', desc: 'Proper business structure offers tax benefits including deductible business expenses, retirement plan options, and favorable tax treatment.' },
              { icon: Building2, title: 'Business Credibility', desc: 'Formal business entities enhance credibility with customers, suppliers, and investors, making it easier to do business and secure funding.' },
              { icon: FileCheck, title: 'Legal Compliance', desc: 'We ensure your business meets all state and federal formation requirements, avoiding costly mistakes and legal issues.' },
              { icon: Check, title: 'Banking & Contracts', desc: 'Established business entities can open business bank accounts, enter contracts, and conduct business transactions in the company name.' },
              { icon: Shield, title: 'Perpetual Existence', desc: 'Corporations and LLCs continue to exist independently of their owners, providing continuity for business operations and ownership transfers.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <item.icon className="h-12 w-12 text-accent mb-6" />
                <h3 className="text-xl font-bold text-primary mb-4">{item.title}</h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              What Our Company Formation Services Include
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { title: 'Business Name Search & Registration', desc: 'We conduct comprehensive business name availability searches and reserve your business name with the appropriate state agency.' },
              { title: 'Articles of Organization/Incorporation', desc: 'Our legal team prepares and files your formation documents (Articles of Organization for LLCs, Articles of Incorporation for Corporations) with the state.' },
              { title: 'Registered Agent Service', desc: 'We provide registered agent service to receive legal documents and state correspondence on behalf of your business.' },
              { title: 'EIN Application', desc: 'We handle your federal Employer Identification Number (EIN) application with the IRS, which is required for taxes and banking.' },
              { title: 'Operating Agreement/Bylaws', desc: 'We prepare customized operating agreements (for LLCs) or corporate bylaws (for Corporations) outlining ownership and management structure.' },
              { title: 'Compliance & Documentation', desc: 'We ensure all state-specific requirements are met and provide you with complete formation documentation and certificates.' }
            ].map((item, i) => (
              <div key={i} className="bg-white border border-border p-8">
                <h3 className="text-xl font-bold text-primary mb-4 flex items-center gap-3">
                  <Check className="h-6 w-6 text-accent" />
                  {item.title}
                </h3>
                <p className="text-base leading-relaxed text-muted-foreground">{item.desc}</p>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Simple 3-Step Company Formation Process
            </h2>
          </div>

          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">1</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Choose Your Business Structure</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Tell us about your business and we'll help you choose between LLC or Corporation formation. We'll discuss your business name, ownership structure, and state of formation.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">2</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">We Handle All Filing & Documentation</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Our team prepares all formation documents, files with the state, obtains your EIN, and prepares your operating agreement or bylaws. We handle all paperwork and government filings.
                </p>
              </div>
            </div>

            <div className="bg-white border border-border p-8 relative">
              <div className="absolute -top-6 left-8 bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center text-2xl font-bold">3</div>
              <div className="mt-6">
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-4">Start Operating Your Business</h3>
                <p className="text-base leading-relaxed text-muted-foreground">
                  Once approved, you receive your formation documents, EIN, and certificate of formation. Your business is now legally established and ready to operate.
                </p>
              </div>
            </div>
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <MobileCarousel className="grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-border p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-accent text-accent" />
                  ))}
                </div>
                <p className="text-base leading-relaxed text-muted-foreground mb-6">
                  "Exceptional company formation service! They handled everything from name registration to EIN application. My LLC was established quickly and all documentation was prepared professionally."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center text-accent font-bold">
                    {['JM', 'SL', 'RP'][i-1]}
                  </div>
                  <div>
                    <div className="font-semibold text-primary">{['Jennifer Miller', 'Steven Lee', 'Rachel Powell'][i-1]}</div>
                    <div className="text-sm text-muted-foreground">{['Entrepreneur', 'Business Owner', 'Startup Founder'][i-1]}</div>
                  </div>
                </div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-primary mb-6">
              Company Formation FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'Should I form an LLC or Corporation?', a: 'LLCs offer simpler management, pass-through taxation, and flexibility, making them ideal for small businesses and startups. Corporations provide more formal structure, easier access to investment capital, and are better for businesses planning to go public. We can help you choose based on your specific business goals.' },
              { q: 'How long does company formation take?', a: 'Most states process LLC and Corporation formations within 1-2 weeks, though some offer expedited processing for additional fees. The entire process including EIN application and document preparation typically takes 2-4 weeks from start to finish.' },
              { q: 'What is a registered agent and do I need one?', a: 'A registered agent is an individual or business designated to receive legal documents, government correspondence, and service of process on behalf of your business. Most states require LLCs and Corporations to maintain a registered agent with a physical address in the state of formation.' },
              { q: 'Do I need to register my business name separately?', a: 'If you are doing business under a name different from your LLC or Corporation name, you may need to file a DBA (Doing Business As) or trade name registration. We can help you determine if additional name registrations are required in your state.' }
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i+1}`} className="bg-white border border-border px-8">
                <AccordionTrigger className="text-left hover:no-underline py-6">
                  <span className="text-lg font-semibold text-primary">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground pb-6">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 md:py-32 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-6">
            Ready to Start Your Business?
          </h2>
          <p className="text-lg leading-relaxed text-white/90 mb-8">
            Get your LLC or Corporation formed quickly with professional business formation services. Start today!
          </p>
          <Button size="lg" onClick={onCTAClick} className="bg-accent hover:bg-accent/90 text-primary">
            Form My Company Now
          </Button>
        </div>
      </section>
    </div>
  );
}
